import React, { useState, useEffect, useRef } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    TextField,
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    Grid,
    Typography,
    Tooltip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { getOcrDocumentUploads, downloadOcrDocumentOriginal, returnUploadToInbox, downloadTranscriptionPreview } from '../services/transcriptionService';
import GetAppIcon from '@material-ui/icons/GetApp';
import Auth from '../services/auth';
import PubSub from 'pubsub-js';
import moment from 'moment';
import ClearIcon from '@material-ui/icons/Clear';
import HighlightOffTwoToneIcon from '@material-ui/icons/HighlightOffTwoTone';
import AutorenewTwoToneIcon from '@material-ui/icons/AutorenewTwoTone';

const auth = new Auth();

// The WebApi tags the Inbox outcome it creates for "Resubmit for manual processing" (formerly
// "Send to Inbox") with this PatientRecord.
export const RETURNED_TO_INBOX_RECORD = 'Returned to Inbox for manual processing';
// ...and a Transcription upload's resubmit (its original goes to SharePoint) with this one.
export const RESUBMITTED_MANUAL_RECORD = 'Resubmitted for manual processing';

// "Resubmit for manual processing": the fixed reasons the WebApi accepts (code -> label).
export const RESUBMIT_REASONS = [
    { code: 'WrongPatient', label: 'Wrong patient' },
    { code: 'PagesSplitIncorrectly', label: 'Pages split incorrectly' },
    { code: 'WrongProvider', label: 'Wrong provider' },
    { code: 'WrongDateOfService', label: 'Wrong date of service' },
    { code: 'WrongWorkType', label: 'Wrong work type' },
    { code: 'MissingPages', label: 'Missing pages' },
    { code: 'DuplicateUpload', label: 'Duplicate upload' },
    { code: 'Other', label: 'Other' }
];
export const RESUBMIT_DETAILS_MAX = 500;

// Null when the reason/details can be sent; otherwise what to tell the user.
export const resubmitValidation = (reasonCode, details) => {
    if (!RESUBMIT_REASONS.some((reason) => reason.code === reasonCode)) return 'Choose a reason.';
    const trimmed = (details || '').trim();
    if (trimmed.length > RESUBMIT_DETAILS_MAX) return `Details must be ${RESUBMIT_DETAILS_MAX} characters or fewer.`;
    if (reasonCode === 'Other' && !trimmed) return 'Enter details when the reason is Other.';
    return null;
};

const outcomesOf = (upload) => {
    if (Array.isArray(upload.Outcomes) && upload.Outcomes.length > 0) return upload.Outcomes;
    return upload.JobId ? [{ Kind: 'Job', JobId: upload.JobId, ReportId: upload.ReportId }] : [];
};

// ADO 6118: why an Inbox outcome went to the Inbox. The WebApi maps a fixed reason code to this
// text ("Sent to Inbox: no patient match"); anything else is not shown.
export const inboxReasonLabel = (item) => (item && item.Kind === 'Inbox' && typeof item.InboxReasonLabel === 'string'
    && item.InboxReasonLabel.startsWith('Sent to Inbox: ') ? item.InboxReasonLabel : null);

// Transcription Upload: a record with an issue was not made into a job; its original file went to
// the Dovlin SharePoint folder for manual processing. The WebApi maps a fixed reason code to this
// text ("Sent to SharePoint for manual processing: patient not identified"); anything else is not shown.
// The vendor is never named on screen; a label in an earlier wording is shown in the current wording.
export const MANUAL_PROCESSING_KIND = 'ManualProcessing';
const MANUAL_PROCESSING_PREFIX = 'Sent to SharePoint for manual processing';
const EARLIER_MANUAL_PROCESSING_PREFIXES = ['Sent to Dovlin for manual processing', 'Sent for manual processing'];
export const manualProcessingLabel = (item) => {
    if (!item || item.Kind !== MANUAL_PROCESSING_KIND || typeof item.ManualProcessingLabel !== 'string') return null;
    const label = item.ManualProcessingLabel;
    if (label.startsWith(MANUAL_PROCESSING_PREFIX)) return label;
    const earlier = EARLIER_MANUAL_PROCESSING_PREFIXES.find((prefix) => label.startsWith(prefix));
    return earlier ? MANUAL_PROCESSING_PREFIX + label.slice(earlier.length) : null;
};

// The Kind column's text for an outcome.
export const outcomeKindLabel = (kind) => (kind === MANUAL_PROCESSING_KIND ? 'Manual processing' : kind);

// Transcription "Preview only (don't create a job)": the upload ran as a real one but wrote nothing.
// Each record's outcome is Kind "Preview" with fixed codes from the WebApi (no patient data): what
// would have happened, how the patient was found, provider, narrative, work type and any issue.
export const PREVIEW_KIND = 'Preview';
const MATCH_SOURCE_LABELS = {
    Document: 'document', Schedule: 'schedule', FileNameMrn: 'file name MRN', FileNameName: 'file name patient name',
    FileNameIndex: 'file name index', None: 'none'
};
export const uploadStatusLabel = (upload) => {
    if (!upload) return null;
    if (upload.Status === 'Preview') return 'Preview ready';
    if (upload.IsPreview && ['Received', 'Started', 'Pending'].includes(upload.Status)) return 'Preview processing';
    return upload.Status;
};
export const previewSummary = (preview) => {
    if (!preview) return 'Preview';
    const issue = preview.IssueLabel || preview.Issue;
    const head = preview.Outcome === 'Job' ? 'Would create a job'
        : preview.Outcome === 'ManualProcessing' ? `Would go to manual processing${issue ? `: ${issue}` : ''}`
        : `No job${issue ? `: ${issue}` : ''}`;
    const parts = [head, `patient match: ${MATCH_SOURCE_LABELS[preview.MatchSource] || 'none'}`];
    if (preview.Provider) parts.push(`provider: ${preview.Provider}`);
    if (preview.DeliveredNarrative) parts.push(`narrative: ${preview.DeliveredNarrative}${preview.NarrativeDelivery ? ` (${preview.NarrativeDelivery})` : ''}`);
    if (preview.WorkType) parts.push(`work type: ${preview.WorkType}${preview.GenericTemplate ? ' (generic template)' : ''}`);
    if (preview.JobStatus === '3') parts.push('job would go to Proof Admin');
    return parts.join(' · ');
};

// Transcription Upload's "Status Details" column: what happened to a record (or a single-patient upload,
// whose outcome fields the API mirrors onto it), in words an uploader can act on. "Complete" alone
// does not say whether a job was made or the file went to SharePoint for manual processing.
export const statusDetails = (item, status) => {
    if (!item) return null;
    if (item.Kind === PREVIEW_KIND) return previewSummary(item.Preview);
    if (status === 'Failed' || item.Kind === 'Failed') return item.Reason ? `Failed: ${item.Reason}` : 'Failed';
    const manual = manualProcessingLabel(item);
    if (manual) return manual;
    if (item.Kind === MANUAL_PROCESSING_KIND) return MANUAL_PROCESSING_PREFIX;
    if (item.Kind === 'Inbox') return inboxReasonLabel(item) || 'Sent to Inbox';
    if (item.Kind === 'Job' || item.JobId) return 'Job created';
    if (status === 'Started' || status === 'Pending') return 'Processing';
    return null;
};

// A multi-patient upload's parent row: counts by outcome, e.g. "2 jobs created, 1 sent to SharePoint".
export const statusDetailsSummary = (outcomes) => {
    const count = (predicate) => outcomes.filter(predicate).length;
    const parts = [];
    const jobs = count((o) => o.Kind === 'Job');
    const manual = count((o) => o.Kind === MANUAL_PROCESSING_KIND);
    const inbox = count((o) => o.Kind === 'Inbox');
    const failed = count((o) => o.Kind === 'Failed');
    const wouldCreate = count((o) => o.Kind === PREVIEW_KIND && o.Preview && o.Preview.Outcome === 'Job');
    const wouldNot = count((o) => o.Kind === PREVIEW_KIND && !(o.Preview && o.Preview.Outcome === 'Job'));
    if (wouldCreate) parts.push(`Preview: ${wouldCreate} ${wouldCreate === 1 ? 'job' : 'jobs'} would be created`);
    if (wouldNot) parts.push(`${wouldCreate ? '' : 'Preview: '}${wouldNot} without a job`);
    if (jobs) parts.push(`${jobs} ${jobs === 1 ? 'job' : 'jobs'} created`);
    if (manual) parts.push(`${manual} sent to SharePoint`);
    if (inbox) parts.push(`${inbox} sent to Inbox`);
    if (failed) parts.push(`${failed} failed`);
    return parts.length ? parts.join(', ') : null;
};

export const isReturnedToInbox = (upload) => outcomesOf(upload).some((outcome) => outcome.PatientRecord === RETURNED_TO_INBOX_RECORD
    || outcome.PatientRecord === RESUBMITTED_MANUAL_RECORD
    || (outcome.Kind === MANUAL_PROCESSING_KIND && outcome.ManualProcessingReason === 'Resubmitted'));

// The server decides; this only offers the action where it can apply: a finished Coding or Transcription
// upload that created at least one job (coded, exported or already removed jobs included) and has not been
// resubmitted (no Inbox outcome yet for Coding).
export const returnableJobIds = (upload) => {
    const outcomes = outcomesOf(upload);
    if (!['Complete', 'Failed'].includes(upload.Status) || isReturnedToInbox(upload)
        || outcomes.some((outcome) => outcome.Kind === 'Inbox')) return [];
    return [...new Set(outcomes.filter((outcome) => outcome.Kind === 'Job' && outcome.JobId).map((outcome) => outcome.JobId))];
};
export const resubmittableJobIds = returnableJobIds;

const isHome998 = () => {
    try {
        return typeof auth.getHomeAccount === 'function' && auth.getHomeAccount() === '998';
    } catch (error) {
        return false;
    }
};

const useStyles = makeStyles((theme) => ({
    searchContainer: {
        display: 'flex',
        alignItems: 'flex-end',
        marginBottom: theme.spacing(1),
        gap: theme.spacing(2)
    },
    searchField: {
        minWidth: 200
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(2)
    },
    errorText: {
        color: theme.palette.error.main,
        fontSize: '0.875rem',
        marginTop: theme.spacing(1)
    },
    tableRow: {
        height: 36,
        '&:hover': {
            backgroundColor: theme.palette.action.hover
        }
    },
    // One child row per patient under a multi-patient upload: always shown, indented and
    // de-emphasized so the grouping reads at a glance. No expand/collapse.
    outcomeRow: {
        height: 32,
        backgroundColor: theme.palette.action.hover,
        '& td': {
            color: theme.palette.text.secondary,
            borderBottomStyle: 'dashed'
        }
    },
    outcomeLabelCell: {
        paddingLeft: theme.spacing(5)
    },
    iconSizeSmall: {
        width: 24,
        height: 24
    }
}));

const BatchCodingUploadsTable = ({ uploadSource = 'CodingUpload' }) => {
    const classes = useStyles();

    const defaultSearchCreatedBy = auth.getUserEmail();
    const defaultUploadStartDate = moment().subtract(1, 'days').format('YYYY-MM-DD');
    const defaultUploadEndDate = moment().format('YYYY-MM-DD');

    const [uploads, setUploads] = useState([]);
    const [searchStatus, setSearchStatus] = useState('');
    const [searchCreatedBy, setSearchCreatedBy] = useState(defaultSearchCreatedBy);
    const [uploadStartDate, setUploadStartDate] = useState(defaultUploadStartDate);
    const [uploadEndDate, setUploadEndDate] = useState(defaultUploadEndDate);
    const [searchOriginalClinicalFileName, setSearchOriginalClinicalFileName] = useState('');
    const [dateError, setDateError] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [loading, setLoading] = useState(false);

    const [totalCount, setTotalCount] = useState(0);
    const [historyError, setHistoryError] = useState('');
    const [lastLoaded, setLastLoaded] = useState('');
    const defaults = () => ({ CreatedBy: auth.getUserEmail(), Status: '', OriginalClinicalFileName: '',
        UploadStartDate: moment().subtract(1, 'days').format('YYYY-MM-DD'), UploadEndDate: moment().add(1, 'days').format('YYYY-MM-DD') });
    const query = useRef({ filters: defaults(), page: 0, size: 10, snapshot: null, account: auth.getActiveAccount() });
    const generation = useRef(0), pending = useRef(false), mounted = useRef(true);
    const fetchUploads = async (reset = false, newPage = query.current.page, newSize = query.current.size, filters = query.current.filters) => {
        if (pending.current) return;
        const current = query.current;
        if (reset) newPage = 0;

        const requestGeneration = ++generation.current, account = current.account;
        pending.current = true; setLoading(true); setHistoryError('');
        try {
            const response = await getOcrDocumentUploads({ ...filters, AccountNumber: account,
                UploadSource: uploadSource, Page: newPage + 1, PageSize: newSize, SnapshotMaxId: reset ? null : current.snapshot });
            if (!mounted.current || generation.current !== requestGeneration || auth.getActiveAccount() !== account) return;
            const data = response && response.data;
            if (!data || !Array.isArray(data.Items) || !Number.isInteger(data.TotalCount)) throw new Error('Invalid history response');
            current.snapshot = data.SnapshotMaxId; current.filters = filters;
            if (newPage > 0 && newPage * newSize >= data.TotalCount) {
                current.page = 0; current.size = newSize;
                setPage(0); setRowsPerPage(newSize); setUploads([]); setTotalCount(0);
                setHistoryError('This page is no longer available because upload history changed. Select Refresh to reload the first page.');
            } else {
                current.page = newPage; current.size = newSize;
                setPage(newPage); setRowsPerPage(newSize); setUploads(data.Items); setTotalCount(data.TotalCount);
            }
            setLastLoaded(new Date().toLocaleTimeString());
        } catch (error) {
            if (mounted.current && generation.current === requestGeneration)
                setHistoryError('Upload history could not be refreshed. Previously displayed results may be stale. Select Refresh to retry.');
        } finally {
            if (mounted.current && generation.current === requestGeneration) { pending.current = false; setLoading(false); }
        }
    };
    const resetFilters = () => {
        const value = defaults(); setSearchStatus(''); setSearchCreatedBy(value.CreatedBy); setSearchOriginalClinicalFileName('');
        setUploadStartDate(value.UploadStartDate); setUploadEndDate(moment(value.UploadEndDate).subtract(1, 'days').format('YYYY-MM-DD'));
        setDateError(''); return value;
    };
    const handleReset = () => { if (pending.current) return; fetchUploads(true, 0, query.current.size, resetFilters()); };
    useEffect(() => {
        mounted.current = true; pending.current = false; setUploads([]); setTotalCount(0); setLastLoaded(''); fetchUploads(true);
        const refreshToken = PubSub.subscribe('BatchCodingUploadStatusRefresh', () => fetchUploads(true));
        const changingToken = PubSub.subscribe('AccountChanging', () => {
            generation.current++; pending.current = true;
            setUploads([]); setTotalCount(0); setLastLoaded(''); setHistoryError(''); setLoading(true);
        });
        const accountToken = PubSub.subscribe('AccountChanged', () => {
            generation.current++; pending.current = false; query.current.account = auth.getActiveAccount();
            setUploads([]); setTotalCount(0); setLastLoaded(''); setHistoryError(''); fetchUploads(true, 0, query.current.size, resetFilters());
        });
        return () => { mounted.current = false; generation.current++; PubSub.unsubscribe(refreshToken); PubSub.unsubscribe(accountToken); PubSub.unsubscribe(changingToken); };
    }, [uploadSource]);
    const handleSearch = () => {
        if (pending.current) return;
        const start = moment(uploadStartDate, 'YYYY-MM-DD', true), end = moment(uploadEndDate, 'YYYY-MM-DD', true);
        if (!start.isValid() || !end.isValid() || end.isBefore(start) || end.diff(start, 'days') > 30) {
            setDateError('Choose a valid date range of no more than 31 days.'); return;
        }
        setDateError(''); const filters = { CreatedBy: searchCreatedBy.trim(), OriginalClinicalFileName: searchOriginalClinicalFileName.trim(),
            Status: searchStatus.trim(), UploadStartDate: start.format('YYYY-MM-DD'), UploadEndDate: end.add(1, 'days').format('YYYY-MM-DD') };
        fetchUploads(true, 0, query.current.size, filters);
    };
    const handleChangePage = (event, newPage) => fetchUploads(false, newPage);
    const handleChangeRowsPerPage = event => fetchUploads(true, 0, parseInt(event.target.value, 10));

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    const [downloadError, setDownloadError] = useState('');
    const [returnConfirm, setReturnConfirm] = useState(null);
    const [returning, setReturning] = useState(false);
    const [returnMessage, setReturnMessage] = useState('');
    const [returnError, setReturnError] = useState('');
    const [resubmitReason, setResubmitReason] = useState('');
    const [resubmitDetails, setResubmitDetails] = useState('');
    const home998 = isHome998();
    // Transcription too (2026-10-28): its jobs are removed and the original goes to SharePoint, not the Inbox.
    const canReturnToInbox = home998;
    const resubmitDestination = uploadSource === 'TranscriptionUpload'
        ? 'sent to SharePoint for manual processing' : 'sent to the Inbox for manual processing';
    // 998 staff can download the original of any upload (Coding and Transcription), not only Failed rows.
    const canDownloadAnyOriginal = home998;
    // Transcription only: the Coding screen keeps its columns unchanged.
    const showStatusDetails = uploadSource === 'TranscriptionUpload';

    const requestReturnToInbox = (upload, jobIds) => {
        setReturnMessage(''); setReturnError(''); setResubmitReason(''); setResubmitDetails('');
        setReturnConfirm({ upload, jobIds, account: query.current.account });
    };

    const resubmitInvalid = resubmitValidation(resubmitReason, resubmitDetails);

    const confirmReturnToInbox = async () => {
        if (resubmitInvalid) return;
        const { upload, jobIds, account } = returnConfirm;
        const reasonCode = resubmitReason, details = resubmitDetails.trim();
        setReturnConfirm(null); setReturning(true);
        try {
            const response = await returnUploadToInbox(upload.Id, account, { reasonCode, details });
            const data = (response && response.data) || {};
            const removed = (data.JobIds && data.JobIds.length ? data.JobIds : jobIds).join(', ');
            if (mounted.current) setReturnMessage(typeof data.Message === 'string' && data.Message ? data.Message
                : uploadSource === 'TranscriptionUpload'
                    ? `Resubmitted for manual processing. Job(s) ${removed} removed. The original file was sent to SharePoint for manual processing.`
                    : `Resubmitted for manual processing. Job(s) ${removed} removed. The original document was sent to the Inbox as document ${data.InboxDocumentId}.`);
        } catch (error) {
            const data = error && error.response && error.response.data;
            if (mounted.current) setReturnError(data && typeof data.Message === 'string' && data.Message
                ? data.Message : 'The upload could not be resubmitted. Refresh the status before trying again.');
        } finally {
            if (mounted.current) setReturning(false);
            // Show the removed job(s) and the new Inbox document, or whatever state the server kept.
            if (mounted.current && auth.getActiveAccount() === account) fetchUploads(false);
        }
    };

    const renderReturnAction = (upload) => {
        const jobIds = canReturnToInbox ? returnableJobIds(upload) : [];
        if (jobIds.length === 0) return null;
        return (
            <Tooltip title={`Remove all of the upload's jobs; the original document is ${resubmitDestination}`} placement="top">
                <span>
                    <Button size="small" color="secondary" disabled={returning || loading}
                        onClick={() => requestReturnToInbox(upload, jobIds)} data-testid={`return-to-inbox-${upload.Id}`}>
                        Resubmit for manual processing
                    </Button>
                </span>
            </Tooltip>
        );
    };

    // The original document is one file per upload (OcrDocuments row), not per outcome -- a
    // multi-patient batch scan has one archived original regardless of which patient's outcome
    // failed, so this always downloads by the parent upload's id.
    const handleDownload = async (upload) => {
        setDownloadError('');
        try {
            await downloadOcrDocumentOriginal(upload.Id, auth.getActiveAccount(), upload.OriginalClinicalFileName);
        } catch (error) {
            setDownloadError(`Could not download the original document for ${upload.FileName}.`);
        }
    };

    const renderDownloadAction = (upload) => (
        <Tooltip title="Download original document" placement="top">
            <IconButton size="small" onClick={() => handleDownload(upload)} data-testid={`download-original-${upload.Id}`}>
                <GetAppIcon className={classes.iconSizeSmall} />
            </IconButton>
        </Tooltip>
    );

    // "Download preview": the Word document a preview record's job would have had. Offered only where
    // the WebApi says this user may download it (PreviewDownloadAllowed) and a document was stored.
    const handlePreviewDownload = async (upload, page) => {
        setDownloadError('');
        try {
            await downloadTranscriptionPreview(upload.Id, auth.getActiveAccount(), page);
        } catch (error) {
            setDownloadError(`Could not download the preview document for ${upload.FileName}.`);
        }
    };

    const renderPreviewDownload = (upload, item) => {
        const preview = item && item.Kind === PREVIEW_KIND ? item.Preview : null;
        if (!upload.PreviewDownloadAllowed || !preview || !preview.Artifact) return null;
        return (
            <Button size="small" color="primary" startIcon={<GetAppIcon />} onClick={() => handlePreviewDownload(upload, preview.Page)}
                data-testid={`download-preview-${upload.Id}-${preview.Page}`}>
                Download preview
            </Button>
        );
    };

    // Failed rows already carry the download button next to their reason.
    const renderStaffDownloadAction = (upload) => (canDownloadAnyOriginal && upload.Status !== 'Failed' ? renderDownloadAction(upload) : null);

    // `item` is either an upload (single-patient: outcome fields mirrored onto it by the API) or one
    // of a multi-patient upload's Outcomes - both carry the same field names.
    const renderAssignmentStatus = (item) => {
        const status = <Typography variant="body2" noWrap>{item.AssignmentStatus}</Typography>;
        const withReason = (content) => (
            <React.Fragment>
                {content}
                {inboxReasonLabel(item) && (
                    <Typography variant="caption" display="block" data-testid="inbox-reason">{inboxReasonLabel(item)}</Typography>
                )}
                {!showStatusDetails && manualProcessingLabel(item) && (
                    <Typography variant="body2" display="block" data-testid="manual-processing-reason">{manualProcessingLabel(item)}</Typography>
                )}
            </React.Fragment>
        );
        if (!item.LastUpdateDate) {
            return withReason(status);
        }
        return withReason(
            <Tooltip title={`Last update: ${item.LastUpdatedBy} on ${moment(item.LastUpdateDate).format('MM/DD/YYYY h:mm:ss A')}`} placement="top">{status}</Tooltip>
        )
    }

    const renderInboxDocumentIsDeletedStatus = (item) => {
        return (
            item.IsInboxDocumentDeleted ?
                <Tooltip title="document deleted - not visible in coding inbox" placement="top"><HighlightOffTwoToneIcon style={{ color: 'red' }} /></Tooltip> :
                <Tooltip title="document available in coding inbox - pending assignment" placement="top"><AutorenewTwoToneIcon /></Tooltip>
        )
    }

    // The report link needs the ReportId; jobs recorded before it was stored show the id as text.
    const renderJobId = (item, removed = false) => {
        if (!item.JobId) {
            return null;
        }
        if (removed) {
            return <Typography variant="body2" noWrap>{`${item.JobId} (removed)`}</Typography>;
        }
        if (!item.ReportId) {
            return <Typography variant="body2" noWrap>{item.JobId}</Typography>;
        }
        return (
            <Link to={`/reportDetails/${auth.getActiveAccount()}/${item.ReportId}`} data-testid="job-link">
                <Typography variant="body2" noWrap>
                    {item.JobId}
                </Typography>
            </Link>
        );
    }

    const isMultiPatient = (upload) => Array.isArray(upload.Outcomes) && upload.Outcomes.length > 1;

    const patientOutcomes = (upload) => upload.Outcomes.filter((outcome) => outcome.PatientRecord !== RETURNED_TO_INBOX_RECORD);

    const outcomeLabel = (upload, outcome) => {
        if (outcome.PatientRecord === RETURNED_TO_INBOX_RECORD) return 'Original document - sent to Inbox for manual processing';
        const patients = patientOutcomes(upload);
        const who = outcome.PatientRecord ? ` - ${outcome.PatientRecord}` : '';
        return `Patient ${patients.indexOf(outcome) + 1} of ${patients.length}${who}`;
    }

    const renderOutcomeRow = (upload, outcome, index) => (
        <TableRow
            key={`${outcome.Kind}-${outcome.OutcomeId}`}
            className={classes.outcomeRow}
            data-testid={`outcome-row-${upload.Id}`}
        >
            <TableCell className={classes.outcomeLabelCell}>{outcomeLabel(upload, outcome)}</TableCell>
            <TableCell>{outcomeKindLabel(outcome.Kind)}</TableCell>
            <TableCell />
            <TableCell />
            <TableCell>{outcome.InboxDocumentId}</TableCell>
            <TableCell />
            <TableCell />
            <TableCell>{renderJobId(outcome, outcome.Kind === 'Job' && isReturnedToInbox(upload))}</TableCell>
            <TableCell>{outcome.Kind === 'Inbox' && Number(outcome.InboxDocumentId) > 0 ? renderInboxDocumentIsDeletedStatus(outcome) : null}</TableCell>
            <TableCell data-testid="assignment-status">
                {outcome.Kind === 'Failed'
                    ? <Grid container alignItems="center" wrap="nowrap"><Typography variant="body2">{outcome.Reason}</Typography>{renderDownloadAction(upload)}</Grid>
                    : renderAssignmentStatus(outcome)}
            </TableCell>
            {showStatusDetails && (
                <TableCell data-testid="status-details">
                    <Typography variant="body2">{statusDetails(outcome)}</Typography>
                    {renderPreviewDownload(upload, outcome)}
                </TableCell>
            )}
        </TableRow>
    );

    const renderUploadRows = (upload) => {
        const multi = isMultiPatient(upload);
        const rows = [
            <TableRow key={upload.Id} className={classes.tableRow} data-testid={`upload-row-${upload.Id}`}>
                <TableCell>{upload.FileName}</TableCell>
                <TableCell data-testid="upload-status">{uploadStatusLabel(upload)}</TableCell>
                <TableCell>{upload.OriginalClinicalFileName}</TableCell>
                <TableCell>{upload.SelectedWorkType || 'not captured'}</TableCell>
                <TableCell>{multi ? null : upload.InboxDocumentId}</TableCell>
                <TableCell>{upload.CreatedBy}</TableCell>
                <TableCell>{moment(upload.CreateDate).format('MM/DD/YYYY h:mm:ss A')}</TableCell>
                <TableCell>{multi ? <Typography variant="body2" noWrap>{patientOutcomes(upload).length === 1 ? '1 patient' : `${patientOutcomes(upload).length} patients`}</Typography> : renderJobId(upload)}</TableCell>
                <TableCell>{multi || upload.Kind !== 'Inbox' || Number(upload.InboxDocumentId) <= 0 ? null : renderInboxDocumentIsDeletedStatus(upload)}</TableCell>
                <TableCell data-testid="assignment-status">
                    {multi ? null : (
                        upload.Status === 'Failed'
                            ? <Grid container alignItems="center" wrap="nowrap"><Typography variant="body2">{upload.Reason}</Typography>{renderDownloadAction(upload)}</Grid>
                            : renderAssignmentStatus(upload)
                    )}
                    {renderStaffDownloadAction(upload)}
                    {renderReturnAction(upload)}
                </TableCell>
                {showStatusDetails && (
                    <TableCell data-testid="status-details">
                        <Typography variant="body2">{multi ? statusDetailsSummary(upload.Outcomes) : statusDetails(upload, upload.Status)}</Typography>
                        {!multi && renderPreviewDownload(upload, upload)}
                    </TableCell>
                )}
            </TableRow>
        ];
        if (multi) {
            upload.Outcomes.forEach((outcome, index) => rows.push(renderOutcomeRow(upload, outcome, index)));
        }
        return rows;
    };

    // Pagination and the status filter work on uploads (documents); child rows come along with
    // their parent and do not count toward the page size.
    const paginatedUploads = uploads;

    return (
        <Paper>
            <div className={classes.searchContainer}>
                <TextField
                    className={classes.searchField}
                    variant="outlined"
                    label="Upload Status"
                    value={searchStatus}
                    onChange={(e) => setSearchStatus(e.target.value)}
                    onKeyPress={handleKeyPress}
                    size="small"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setSearchStatus('')}>
                                    <ClearIcon />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
                <TextField
                    className={classes.searchField}
                    variant="outlined"
                    id={`upload-history-${uploadSource}-uploader`} label="UploadedBy"
                    value={searchCreatedBy}
                    onChange={(e) => setSearchCreatedBy(e.target.value)}
                    onKeyPress={handleKeyPress}
                    size="small"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setSearchCreatedBy('')}>
                                    <ClearIcon />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
                <TextField
                    className={classes.searchField}
                    variant="outlined"
                    id={`upload-history-${uploadSource}-start`} label="Start Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={uploadStartDate}
                    onChange={(e) => setUploadStartDate(e.target.value)}
                    onKeyPress={handleKeyPress}
                    size="small"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setUploadStartDate(defaultUploadStartDate)}>
                                    <ClearIcon />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
                <TextField
                    className={classes.searchField}
                    variant="outlined"
                    id={`upload-history-${uploadSource}-end`} label="End Date"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={uploadEndDate}
                    onChange={(e) => setUploadEndDate(e.target.value)}
                    onKeyPress={handleKeyPress}
                    size="small"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setUploadEndDate(defaultUploadEndDate)}>
                                    <ClearIcon />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
                <TextField
                    className={classes.searchField}
                    variant="outlined"
                    id={`upload-history-${uploadSource}-filename`} label="Uploaded File Name"
                    value={searchOriginalClinicalFileName}
                    onChange={(e) => setSearchOriginalClinicalFileName(e.target.value)}
                    onKeyPress={handleKeyPress}
                    size="small"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setSearchOriginalClinicalFileName('')}>
                                    <ClearIcon />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
                <Button variant="contained" color="primary" onClick={handleSearch} disabled={loading}>
                    Search
                </Button>
                <Button variant="contained" color="secondary" onClick={handleReset} disabled={loading}>
                    Reset
                </Button>
            </div>
            <Button onClick={() => fetchUploads(true)} disabled={loading}>Refresh</Button>
            <Typography variant="caption">{lastLoaded ? 'Last loaded: ' + lastLoaded : 'Not yet loaded'}</Typography>
            {historyError && <Typography role="alert" color="error">{historyError}</Typography>}
            {dateError && <div className={classes.errorText}>{dateError}</div>}
            {downloadError && <div className={classes.errorText}>{downloadError}</div>}
            {returning && <Typography role="status">Resubmitting for manual processing...</Typography>}
            {returnMessage && <Typography role="status" data-testid="return-to-inbox-message">{returnMessage}</Typography>}
            {returnError && <Typography role="alert" color="error" data-testid="return-to-inbox-error">{returnError}</Typography>}
            <Dialog open={Boolean(returnConfirm)} onClose={() => setReturnConfirm(null)} aria-labelledby="return-to-inbox-title" fullWidth maxWidth="sm">
                <DialogTitle id="return-to-inbox-title">Resubmit for manual processing</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" data-testid="return-to-inbox-warning">
                        ALL jobs from this upload will be removed, including jobs that are already coded, exported or billed.
                        The original document will be {resubmitDestination}. This cannot be undone.
                    </Alert>
                    <DialogContentText data-testid="return-to-inbox-prompt" style={{ marginTop: 16 }}>
                        {returnConfirm ? `Job(s) from this upload: ${returnConfirm.jobIds.join(', ')}.` : ''}
                    </DialogContentText>
                    <TextField
                        select
                        fullWidth
                        required
                        margin="dense"
                        variant="outlined"
                        label="Reason"
                        value={resubmitReason}
                        onChange={(e) => setResubmitReason(e.target.value)}
                        SelectProps={{ native: true }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ 'data-testid': 'resubmit-reason', 'aria-label': 'Reason' }}
                    >
                        <option value="">Choose a reason</option>
                        {RESUBMIT_REASONS.map((reason) => <option key={reason.code} value={reason.code}>{reason.label}</option>)}
                    </TextField>
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        margin="dense"
                        variant="outlined"
                        label={resubmitReason === 'Other' ? 'Details (required)' : 'Details (optional)'}
                        required={resubmitReason === 'Other'}
                        value={resubmitDetails}
                        onChange={(e) => setResubmitDetails(e.target.value.slice(0, RESUBMIT_DETAILS_MAX))}
                        error={resubmitDetails.trim().length > RESUBMIT_DETAILS_MAX}
                        helperText={`${resubmitDetails.length}/${RESUBMIT_DETAILS_MAX}`}
                        inputProps={{ maxLength: RESUBMIT_DETAILS_MAX, 'data-testid': 'resubmit-details', 'aria-label': 'Details' }}
                        FormHelperTextProps={{ 'data-testid': 'resubmit-details-counter' }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReturnConfirm(null)} color="primary">Cancel</Button>
                    <Button onClick={confirmReturnToInbox} color="secondary" variant="contained" disabled={Boolean(resubmitInvalid)}
                        data-testid="return-to-inbox-confirm">Resubmit</Button>
                </DialogActions>
            </Dialog>

            {loading ? (
                <div className={classes.loadingContainer}>
                    <CircularProgress />
                </div>
            ) : (
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>PayloadFile</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>UploadedFile</TableCell>
                                <TableCell>Selected Work Type</TableCell>
                                 <TableCell>InboxDocumentId</TableCell>
                                <TableCell>Uploaded By</TableCell>
                                <TableCell>Uploaded Date</TableCell>
                                <TableCell>Job ID</TableCell>
                                <TableCell>InboxDeleted Status</TableCell>
                                <TableCell>Assignment Status</TableCell>
                                {showStatusDetails && <TableCell>Status Details</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedUploads.map((upload) => renderUploadRows(upload))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
            />
        </Paper>
    );
};

export default BatchCodingUploadsTable;



// WEBPACK FOOTER //
// ./src/components/BatchCodingUploadTable.js